import { reportModuleClick, reportModuleView, reportPageDisappear, reportPageView } from "./bridge/lx";
import { LX_CHANNEL } from "./constant";
import { getAppEnv } from "./getBrowserEnv";
import { Level, reportError, reportPerfMetric } from "./owl";
import { isServer } from "./constant";
import { getUrlParam } from "./urlParams";

export interface LxTrackerContext {
  [key: string]: string | null;
}

export default class LxTracker {
  isSupportLxSDK: boolean;
  pageCid: string;
  pageCase: any = null;
  context?: LxTrackerContext;
  reportPVPromise?: Promise<void>;
  reportPVPromiseResolveFunc?: (value?: void | PromiseLike<void>) => void;

  constructor(pageCid: string, context?: LxTrackerContext) {
    // 是否支持 lxSDK 上报
    // 目前除了非外投容器都支持 lxSDK，外投容器需要使用桥方法上报事件
    this.isSupportLxSDK = !getAppEnv().isGrowthContainer;
    this.pageCid = pageCid;
    this.context = context;
    // 确保 mv、mc 上报前，pageView 上报已完成
    this.reportPVPromise = new Promise((resolve) => {
      this.reportPVPromiseResolveFunc = resolve;
    });
  }

  pageView(valLab?: Record<string, any>, environment?: Record<string, any>): void {
    // SSR兼容性：服务端渲染时跳过埋点逻辑
    if (isServer) {
      return;
    }

    try {
      if (this.isSupportLxSDK) {
        // 使用 lxSDK 上报
        const tracker = (window as any).LXAnalytics("getTracker");
        if (!tracker) {
          reportError({
            name: "[lx] pageViewError",
            msg: 'no tracker',
            category: "baseError",
            level: Level.WARN,
          });
          return;
        }
        this.pageCase = tracker("pageView", { ...valLab, custom: { ...this.context, ...valLab?.custom, } }, environment, this.pageCid);
        this.reportPVPromiseResolveFunc?.();
      } else {
        // 不支持灵犀 SDK
        if (getAppEnv().isGrowthContainer) {
          // 如果是外投容器，调用桥方法上报
          reportPageView({
            channelName: LX_CHANNEL,
            cid: this.pageCid,
            valLab: {
              ...valLab,
              custom: { ...this.context, ...valLab?.custom, },
            },
            success: () => {
              // 上报成功，执行 resolve，允许上报其他事件
              this.reportPVPromiseResolveFunc?.();
            },
          });
        }
      }
    } catch (err) {
      reportError({
        name: "[lx] pageViewError",
        msg: err,
        category: "baseError",
        level: Level.WARN,
      });
    }
  }

  pageDisappear(): void {
    this.reportPVPromise?.then(() => {
      if (this.isSupportLxSDK) {
        if (typeof this.pageCase === "function") {
          this.pageCase("pageDisappear");
        }
      } else {
        if (getAppEnv().isGrowthContainer) {
          reportPageDisappear();
        }
      }
    });
  }

  moduleClick(bid: string, valLab?: Record<string, any>, options?: Record<string, any>): void {
    this.reportPVPromise?.then(() => {
      if (this.isSupportLxSDK) {
        if (typeof this.pageCase === "function") {
          this.pageCase("moduleClick", bid, { 
            ...this.context,
            ...valLab,
            custom: { ...this.context, ...valLab?.custom, },
          }, options);
        }
      } else {
        if (getAppEnv().isGrowthContainer) {
          reportModuleClick({
            channelName: LX_CHANNEL,
            cid: this.pageCid,
            bid,
            valLab: {
              ...this.context,
              ...valLab,
              custom: { ...this.context, ...valLab?.custom, },
            },
          });
        }
      }
    });
  }

  moduleView(bid: string, valLab?: Record<string, any>, options?: Record<string, any>): void {
    this.reportPVPromise?.then(() => {
      if (this.isSupportLxSDK) {
        if (typeof this.pageCase === "function") {
          this.pageCase("moduleView", bid, {
            ...this.context,
            ...valLab,
            custom: { ...this.context, ...valLab?.custom, },
          }, options);
        }
      } else {
        if (getAppEnv().isGrowthContainer) {
          reportModuleView({
            channelName: LX_CHANNEL,
            cid: this.pageCid,
            bid,
            valLab: {
              ...this.context,
              ...valLab,
              custom: { ...this.context, ...valLab?.custom, },
            },
          });
        }
      }
    });
  }

  async setUserId(userId?: string | null) {
    // SSR兼容性：服务端渲染时跳过埋点逻辑
    if (isServer) {
      return;
    }

    if (this.isSupportLxSDK && userId) {
      window.LXAnalytics('set', 'uid', userId);
    }
  }

  // 获取uuid
  async getUuid(): Promise<string> {
    // SSR兼容性：服务端渲染时返回空字符串
    if (isServer) {
      return Promise.resolve('');
    }

    return new Promise((resolve) => {
      if (this.isSupportLxSDK) {
        window.LXAnalytics("get", "uuid", function (uuid: string) {
          resolve(uuid);
        });
      } else {
        if (getAppEnv().isGrowthContainer) {
          // 如果是外投容器，从 url 参数中获取 uuid
          const uuid = getUrlParam("uuid") || "";
          resolve(uuid);
        } else {
          resolve("");
        }
      }
    });
  }

  // 获取唯一标识
  async getCuid(): Promise<string> {
    // SSR兼容性：服务端渲染时返回空字符串
    if (isServer) {
      return Promise.resolve('');
    }

    return new Promise((resolve) => {
      if (this.isSupportLxSDK) {
        window.LXAnalytics("get", "lxcuid", function (cuid: string) {
          resolve(cuid);
        });
      } else {
        if (getAppEnv().isGrowthContainer) {
          // 如果是外投容器，从 url 参数中获取 uuid
          const uuid = getUrlParam("uuid") || "";
          resolve(uuid);
        } else {
          resolve("");
        }
      }
    });
  }

  // 获取会话ID
  async getSessionId(): Promise<string> {
    // SSR兼容性：服务端渲染时返回空字符串
    if (isServer) {
      return "";
    }

    if (this.isSupportLxSDK) {
      if (window.fxSessionId) {
        return window.fxSessionId;
      }
      return Promise.resolve(window.fxGetSessionIdPromise);
    } else {
      if (getAppEnv().isGrowthContainer) {
        // 如果是外投容器，从 url 参数中获取 sessionId
        const sessionId = getUrlParam("msid") || "";
        return sessionId;
      } else {
        return "";
      }
    }
  }
}
